import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useFetcher, useRevalidator } from 'react-router';

import { BulkInviteTemplate } from '~/features/user_management/domain/entities/bulk_invite_template';
import { BulkUploadHistory } from '~/features/user_management/domain/entities/bulk_upload_history';
import { Drawer } from '~/shared/common/Drawer';
import { CustomButton } from '~/shared/components/custom_button';

import { BulkUploadHistoryAccordion } from './bulk_upload_history_modal';

interface BulkInviteComponentProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  onSuccess: (jobId?: string | null) => void;
  onError?: (message?: string) => void;
  template?: BulkInviteTemplate | null;
  jobId?: string | null;
}

interface BulkInviteLoaderData {
  bulkUploadHistory: BulkUploadHistory[];
}

export const BulkInviteComponent: React.FC<BulkInviteComponentProps> = ({
  isOpen,
  onClose,
  onBack: _onBack,
  onSuccess,
  onError,
  template,
  jobId,
}) => {
  const { t } = useTranslation(['userManagement', 'common']);
  const { bulkUploadHistory } = useLoaderData<BulkInviteLoaderData>();
  const fetcher = useFetcher();
  const revalidator = useRevalidator();
  const isSubmitting = fetcher.state === 'submitting' || fetcher.state === 'loading';

  const inputRef = useRef<HTMLInputElement | null>(null);

  const [state, setState] = useState({
    linkUrl: '',
    selectedFile: null as File | null,
    error: null as string | null,
    isDownloading: false,
    bulkInviteJobId: jobId || null,
  });

  // Watch fetcher.data (response from action). Action should return { success?: boolean, error?: string, jobId?: string, bulkUploadHistory?: [] }
  useEffect(() => {
    if (!fetcher.data) return;

    const data = fetcher.data as {
      success?: boolean;
      error?: string;
      jobId?: string;
      bulkUploadHistory?: BulkUploadHistory[];
    };

    if (data.success) {
      // Clear input and notify parent
      setState((prev) => ({
        ...prev,
        linkUrl: '',
        selectedFile: null,
        error: null,
        bulkInviteJobId: data.jobId || null,
      }));
      if (inputRef.current) inputRef.current.value = '';
      // parent shows toast
      onSuccess?.(data.jobId || null);
    } else if (data.error) {
      // parent shows error toast
      onError?.(data.error);
    }
    // (do not reset fetcher here — router handles it)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher.data]);

  const validateUrl = (value: string) => {
    if (!value) return null;
    const urlRegex = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+|\d+\.\d+\.\d+\.\d+)(:\d+)?(\/\S*)?$/;
    return value && !urlRegex.test(value) ? t('invalidUrl') : null;
  };

  const handleDownloadTemplate = async () => {
    setState((prev) => ({ ...prev, isDownloading: true }));
    try {
      const templateUrl = template?.url;
      if (!templateUrl) throw new Error('Template not available');
      const link = document.createElement('a');
      link.href = templateUrl;
      link.setAttribute('download', 'BulkUserInviteTemplate.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      // show inline error (optionally parent can be notified)
      setState((prev) => ({ ...prev, error: t('failedToDownloadTemplate') }));
    } finally {
      setState((prev) => ({ ...prev, isDownloading: false }));
    }
  };

  // Submit using fetcher so we can react to fetcher.data
  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();

    const fd = new FormData();
    fd.append('actionType', 'bulkInvite');

    // If file is selected, upload the file
    if (state.selectedFile) {
      console.log('[BulkInvite] Submitting with file:', {
        name: state.selectedFile.name,
        size: state.selectedFile.size,
        type: state.selectedFile.type,
      });
      fd.append('file', state.selectedFile);
    } else if (state.linkUrl.trim()) {
      // Otherwise, use the URL if provided
      const trimmed = state.linkUrl.trim();
      const validationError = validateUrl(trimmed);
      if (validationError) {
        setState((prev) => ({ ...prev, error: validationError }));
        return;
      }
      console.log('[BulkInvite] Submitting with URL:', trimmed);
      fd.append('fileUrl', trimmed);
    } else {
      setState((prev) => ({ ...prev, error: t('pasteAUrl') }));
      return;
    }

    // Submit to same action route your app expects
    fetcher.submit(fd, {
      method: 'post',
      action: '/user-management',
      encType: 'multipart/form-data',
    });
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      position="right"
      width="35rem"
      title={t('bulkUpload')}
      contentClassName="flex flex-col"
    >
      <div className="bulkupload-wrapper container mx-auto flex h-full flex-col">
        {/* Instructions */}
        <div className="mt-4">
          <ol className="list-decimal pl-5 text-neutralSemiDark">
            <li className="bodySm mb-1">{t('bulkStep1')}</li>
            <li className="bodySm">{t('bulkStep2')}</li>
          </ol>
        </div>

        {/* Download Template */}
        <div className="mt-4">
          <CustomButton
            dataName="download-template-btn"
            buttonType="outlined"
            variant="surface"
            className="bodySm border-neutralExtraLight px-4 py-1 text-primaryDefault"
            onClick={handleDownloadTemplate}
            isLoading={state.isDownloading}
          >
            {state.isDownloading ? t('downloadingTemplate') : t('downloadTemplate')}
          </CustomButton>
        </div>

        {/* Input area (we're not using <Form> to control submit) */}
        <form
          onSubmit={handleSubmit}
          className="mt-5 flex h-full flex-col"
        >
          <input
            type="hidden"
            name="actionType"
            value="bulkInvite"
          />
          <div
            className="relative mx-auto mb-8 mt-4 w-full cursor-pointer rounded-xl border-2 border-dashed border-neutralExtraLight bg-white p-6 text-center transition hover:border-primaryDefault"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const file = e.dataTransfer.files[0];
              if (!file) return;

              // Validate file type (CSV only)
              const validTypes = ['text/csv'];
              const isValidType = validTypes.includes(file.type) || file.name.endsWith('.csv');

              if (!isValidType) {
                setState((prev) => ({
                  ...prev,
                  error: t('validCsvFileError'),
                }));
                return;
              }

              // Validate file size (max 50 MB)
              const maxSize = 50 * 1024 * 1024; // 50 MB in bytes
              if (file.size > maxSize) {
                setState((prev) => ({
                  ...prev,
                  error: t('fileSizeExceeded'),
                }));
                return;
              }

              setState((prev) => ({
                ...prev,
                linkUrl: file.name,
                selectedFile: file,
                error: null,
              }));
              if (inputRef.current) {
                inputRef.current.files = e.dataTransfer.files;
              }
            }}
            onClick={() => inputRef.current?.click()} // click anywhere opens file explorer
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              // Ignore keys from inner buttons so they keep their own behavior
              if (e.target !== e.currentTarget) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                inputRef.current?.click();
              }
            }}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;

                // Validate file type (CSV only)
                const validTypes = ['text/csv'];
                const isValidType = validTypes.includes(file.type) || file.name.endsWith('.csv');

                if (!isValidType) {
                  setState((prev) => ({
                    ...prev,
                    error: t('validCsvFileError'),
                  }));
                  return;
                }

                // Validate file size (max 50 MB)
                const maxSize = 50 * 1024 * 1024; // 50 MB in bytes
                if (file.size > maxSize) {
                  setState((prev) => ({
                    ...prev,
                    error: t('fileSizeExceeded'),
                  }));
                  return;
                }

                setState((prev) => ({
                  ...prev,
                  linkUrl: file.name,
                  selectedFile: file,
                  error: null,
                }));
              }}
            />

            <h3 className="bodyMdSemiBold mb-1.5 text-primarySemiDark">
              {t('dragAndDropFileHere')}
            </h3>
            <p className="bodyXs mb-3 text-neutralSemiDark">{t('csvMaxFileSizeLabel')}</p>

            {state.linkUrl ? (
              <div className="inline-flex items-center justify-center gap-3 rounded-xl bg-neutralExtraLight px-4 py-2">
                <p className="bodySm max-w-xs truncate text-fontColor">{state.linkUrl}</p>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation(); // prevent triggering parent click
                    if (inputRef.current) inputRef.current.value = '';
                    setState((prev) => ({ ...prev, linkUrl: '', selectedFile: null, error: null }));
                  }}
                  className="text-btnColor hover:text-errorDefault"
                >
                  ✕
                </button>
              </div>
            ) : (
              <CustomButton
                type="button"
                buttonType="outlined"
                variant="surface"
                className="bodySm border-neutralExtraLight px-4 py-1 text-primaryDefault"
                onClick={(e) => {
                  e.stopPropagation(); // prevent triggering parent click
                  inputRef.current?.click();
                }}
                isLoading={state.isDownloading}
              >
                {t('chooseFile')}
              </CustomButton>
            )}

            {state.error && (
              <div className="bodyMd mx-auto mt-4 w-full max-w-[250px] rounded-xl border border-l-4 border-btnColor border-l-btnColor bg-[#C6343414] px-4 py-2.5 capitalize text-fontColor">
                {state.error}
              </div>
            )}
          </div>

          <BulkUploadHistoryAccordion
            history={(fetcher.data?.bulkUploadHistory as BulkUploadHistory[]) || bulkUploadHistory}
            onRefresh={async () => {
              // ✅ Re-fetch using revalidator
              await revalidator.revalidate();
            }}
          />

          <div className="mt-auto flex justify-end border-t border-neutralExtraLight pb-2 pt-4">
            <CustomButton
              dataName="upload-file-btn"
              buttonType="filled"
              variant="primary"
              className="w-full px-12"
              disabled={!state.linkUrl.trim() || !!state.error || isSubmitting}
              type="submit"
              isLoading={isSubmitting}
            >
              {t('uploadFile')}
            </CustomButton>
          </div>
        </form>
      </div>
    </Drawer>
  );
};
